// Visual workflow editor. Drag-and-drop nodes + edges via reactflow,
// per-node config panel on the right, node palette on the left.
// Used by WorkflowsPage's create/edit modal via the "Visual editor"
// button -- caller renders this in a full-screen overlay and passes
// initial value + onSave/onCancel callbacks.
//
// Pattern matches the CSS-injecting components elsewhere in the PWA
// (ToolActivityStrip / CopyMorphButton): one-time <style> inject on
// module load so re-mounts don't stack duplicate rules.

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
} from "reactflow";
import "reactflow/dist/style.css";

// Friendly labels + per-type config form schema.
const NODE_TYPE_LABELS = {
  "trigger.manual":      { icon: "▶",  label: "Manual trigger",       desc: "Entry point (no inputs)" },
  "trigger.schedule":    { icon: "⏰", label: "Schedule trigger",     desc: "Cron-based (Phase 2c+)" },
  "trigger.webhook":     { icon: "🔗", label: "Webhook trigger",      desc: "External call (Phase 2c+)" },
  "trigger.salesforce":    { icon: "☁️", label: "Salesforce", desc: "Fires on Salesforce events (connect in Marketplace)" },
  "trigger.microsoft":     { icon: "🟦", label: "Microsoft 365", desc: "Fires on Microsoft 365 events (connect in Marketplace)" },
  "trigger.googleWorkspace": { icon: "🟥", label: "Google Workspace", desc: "Fires on Google Workspace events (connect in Marketplace)" },
  "trigger.slack":         { icon: "💬", label: "Slack", desc: "Fires on Slack events (connect in Marketplace)" },
  "trigger.github":        { icon: "🐙", label: "GitHub", desc: "Fires on GitHub events (connect in Marketplace)" },
  "trigger.gitlab":        { icon: "🦊", label: "GitLab", desc: "Fires on GitLab events (connect in Marketplace)" },
  "trigger.jira":          { icon: "🟦", label: "Jira", desc: "Fires on Jira events (connect in Marketplace)" },
  "trigger.confluence":    { icon: "📘", label: "Confluence", desc: "Fires on Confluence events (connect in Marketplace)" },
  "trigger.hubspot":       { icon: "🟧", label: "HubSpot", desc: "Fires on HubSpot events (connect in Marketplace)" },
  "trigger.stripe":        { icon: "💳", label: "Stripe", desc: "Fires on Stripe events (connect in Marketplace)" },
  "trigger.notion":        { icon: "⬛", label: "Notion", desc: "Fires on Notion events (connect in Marketplace)" },
  "trigger.zendesk":       { icon: "🎫", label: "Zendesk", desc: "Fires on Zendesk events (connect in Marketplace)" },
  "trigger.linear":        { icon: "📐", label: "Linear", desc: "Fires on Linear events (connect in Marketplace)" },
  "trigger.asana":         { icon: "🔴", label: "Asana", desc: "Fires on Asana events (connect in Marketplace)" },
  "trigger.monday":        { icon: "🟪", label: "Monday.com", desc: "Fires on Monday.com events (connect in Marketplace)" },
  "trigger.servicenow":    { icon: "🟢", label: "ServiceNow", desc: "Fires on ServiceNow events (connect in Marketplace)" },
  "trigger.zoom":          { icon: "🎥", label: "Zoom", desc: "Fires on Zoom events (connect in Marketplace)" },
  "trigger.shopify":       { icon: "🛍️", label: "Shopify", desc: "Fires on Shopify events (connect in Marketplace)" },
  "trigger.dropbox":       { icon: "📦", label: "Dropbox", desc: "Fires on Dropbox events (connect in Marketplace)" },
  "trigger.airtable":      { icon: "🟨", label: "Airtable", desc: "Fires on Airtable events (connect in Marketplace)" },
  "trigger.twilio":        { icon: "📞", label: "Twilio", desc: "Fires on Twilio events (connect in Marketplace)" },
  "trigger.intercom":      { icon: "🗨️", label: "Intercom", desc: "Fires on Intercom events (connect in Marketplace)" },
  "action.aiChat":       { icon: "🤖", label: "AI chat",              desc: "Run a prompt through Claude" },
  "action.hermesRun":    { icon: "🛠️", label: "Hermes run",           desc: "Launch an autonomous Hermes run" },
  "action.httpRequest":  { icon: "🌐", label: "HTTP request",         desc: "Fetch URL (Phase 2b stub)" },
  "action.mcpCall":      { icon: "🔌", label: "MCP tool call",        desc: "Invoke an MCP server tool" },
  "action.kbSearch":     { icon: "🔍", label: "Knowledge-base search", desc: "Query a KB" },
  "logic.if":            { icon: "❓", label: "If / branch",          desc: "Compare two values" },
  "logic.setVar":        { icon: "📌", label: "Set variable",         desc: "Save a value for later" },
  "output.toast":        { icon: "💬", label: "Toast message",        desc: "Log a message to the run" },
  "output.saveMemory":   { icon: "💾", label: "Save to memory",       desc: "Persist (Phase 2b stub)" },
  "workflow.call":       { icon: "🔁", label: "Subflow (call workflow)", desc: "Run another workflow as a step" },
  "action.sendEmail":    { icon: "📧", label: "Send email",             desc: "Send via the configured mail provider" },
  "action.fetchFeeds":   { icon: "📡", label: "Fetch RSS/Atom feeds",  desc: "Parallel fetch + parse feeds (resilient)" },
  "action.wait":         { icon: "⏱️", label: "Wait",                  desc: "Sleep N ms (0..60s)" },
  "action.code":         { icon: "🧩", label: "Code (sandboxed JS)",   desc: "Transform data with arbitrary JS" },
  "output.note":         { icon: "📝", label: "Note",                  desc: "Sticky note on the canvas" },
  "action.jsonParse":    { icon: "{}", label: "Parse JSON",            desc: "Parse a string into JSON" },
  "action.regex":        { icon: "🔣", label: "Regex match",           desc: "Test / extract via regex" },
  "action.slack":        { icon: "💬", label: "Slack message",         desc: "POST to a Slack incoming webhook" },
  "logic.switch":        { icon: "⇆", label: "Switch / case",         desc: "Multi-way branch by value" },
  "logic.filter":        { icon: "⛛", label: "Filter array",          desc: "Keep items matching a regex" },
  "logic.map":           { icon: "↦", label: "Map array",             desc: "Transform each item via a template" },
  "logic.sort":          { icon: "↕", label: "Sort array",            desc: "Sort by field, asc/desc" },
  "logic.dedupe":        { icon: "≠", label: "Dedupe array",          desc: "Remove duplicates" },
  "action.discord":      { icon: "👾", label: "Discord message",        desc: "POST to a Discord webhook" },
  "action.template":     { icon: "✎", label: "Template format",        desc: "Interpolate a template string" },
  "action.csv":          { icon: "📑", label: "Array → CSV",            desc: "Convert items to CSV text" },
  "action.base64":       { icon: "≡", label: "Base64",                 desc: "Encode / decode" },
  "logic.iif":           { icon: "?", label: "Inline if",              desc: "Pick a value by condition" },
  "logic.length":        { icon: "#", label: "Length",                 desc: "Array / string length" },
  "logic.slice":         { icon: "✂", label: "Slice",                  desc: "Array / string slice" },
  "logic.join":          { icon: "+", label: "Join",                   desc: "Join array with separator" },
  "action.uuid":         { icon: "🆔", label: "Generate UUID",          desc: "uuid v4 string" },
  "action.now":          { icon: "🕒", label: "Now (timestamp)",        desc: "ISO + epoch + parts for tz" },
  "action.replace":      { icon: "↻", label: "Replace",                desc: "Search / replace in a string" },
  "action.markdownToHtml":{ icon: "M", label: "Markdown → HTML",        desc: "Inline markdown to HTML" },
  "output.webhook":      { icon: "📤", label: "Output webhook",         desc: "POST to external URL" },
  "action.urlEncode":    { icon: "%", label: "URL encode",             desc: "URI-component encode" },
  "action.urlDecode":    { icon: "%", label: "URL decode",             desc: "URI-component decode" },
  "action.range":        { icon: "…", label: "Range (array)",          desc: "Generate [start..end-1]" },
  "action.lowercase":    { icon: "↓", label: "Lowercase",              desc: "Lowercase a string" },
  "action.uppercase":    { icon: "↑", label: "Uppercase",              desc: "Uppercase a string" },
  "action.mattermost":   { icon: "🛡", label: "Mattermost message",     desc: "POST to a Mattermost webhook (Marketplace fallback)" },
  "action.rocketchat":   { icon: "🚀", label: "Rocket.Chat message",    desc: "POST to a Rocket.Chat webhook (Marketplace fallback)" },
};

// Per-type config-panel field definitions. { key, label, type, placeholder?, options? }.
const NODE_CONFIG_SCHEMA = {
  "trigger.manual":      [],
  "trigger.schedule":    [{ key: "cron", label: "Cron expression", type: "text", placeholder: "0 9 * * *" }],
  "trigger.webhook":     [{ key: "path", label: "Webhook path", type: "text", placeholder: "/wh/my-hook" }],
  "trigger.salesforce": [
    { key: "event", label: "Event", type: "select", options: ["record.created","record.updated","lead.created","opportunity.stage_changed","case.created"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Salesforce in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.microsoft": [
    { key: "event", label: "Event", type: "select", options: ["outlook.email_received","teams.message","sharepoint.file_added","onedrive.file_added","excel.row_added","calendar.event_created"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Microsoft 365 in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.googleWorkspace": [
    { key: "event", label: "Event", type: "select", options: ["gmail.email_received","calendar.event_created","drive.file_added","sheets.row_added","forms.response"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Google Workspace in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.slack": [
    { key: "event", label: "Event", type: "select", options: ["message.posted","mention","reaction_added","channel.created","slash_command"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Slack in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.github": [
    { key: "event", label: "Event", type: "select", options: ["pull_request.opened","pull_request.merged","issue.opened","push","release.published","workflow_run.completed"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect GitHub in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.gitlab": [
    { key: "event", label: "Event", type: "select", options: ["merge_request.opened","issue.opened","push","pipeline.completed"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect GitLab in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.jira": [
    { key: "event", label: "Event", type: "select", options: ["issue.created","issue.updated","issue.transitioned","comment.added","sprint.started"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Jira in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.confluence": [
    { key: "event", label: "Event", type: "select", options: ["page.created","page.updated","comment.added","space.created"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Confluence in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.hubspot": [
    { key: "event", label: "Event", type: "select", options: ["contact.created","deal.stage_changed","ticket.created","form.submitted"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect HubSpot in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.stripe": [
    { key: "event", label: "Event", type: "select", options: ["payment.succeeded","payment.failed","subscription.created","subscription.cancelled","invoice.paid","refund.created"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Stripe in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.notion": [
    { key: "event", label: "Event", type: "select", options: ["page.created","database.row_added","page.updated"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Notion in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.zendesk": [
    { key: "event", label: "Event", type: "select", options: ["ticket.created","ticket.updated","ticket.solved","comment.added"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Zendesk in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.linear": [
    { key: "event", label: "Event", type: "select", options: ["issue.created","issue.updated","issue.completed","comment.added"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Linear in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.asana": [
    { key: "event", label: "Event", type: "select", options: ["task.created","task.completed","task.updated","project.created"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Asana in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.monday": [
    { key: "event", label: "Event", type: "select", options: ["item.created","item.updated","status_changed","column_changed"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Monday.com in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.servicenow": [
    { key: "event", label: "Event", type: "select", options: ["incident.created","incident.updated","change.requested","problem.created"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect ServiceNow in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.zoom": [
    { key: "event", label: "Event", type: "select", options: ["meeting.started","meeting.ended","recording.completed","participant.joined"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Zoom in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.shopify": [
    { key: "event", label: "Event", type: "select", options: ["order.created","order.fulfilled","product.created","customer.created","cart.abandoned"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Shopify in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.dropbox": [
    { key: "event", label: "Event", type: "select", options: ["file.added","file.updated","folder.created","file.shared"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Dropbox in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.airtable": [
    { key: "event", label: "Event", type: "select", options: ["record.created","record.updated"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Airtable in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.twilio": [
    { key: "event", label: "Event", type: "select", options: ["sms.received","call.completed","whatsapp.received"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Twilio in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "trigger.intercom": [
    { key: "event", label: "Event", type: "select", options: ["conversation.started","conversation.replied","user.created"] },
    { key: "account", label: "Connected account", type: "text", placeholder: "Connect Intercom in Marketplace" },
    { key: "filter", label: "Filter (optional)", type: "text", placeholder: "e.g. status == 'open'" },
  ],
  "action.aiChat":       [
    { key: "prompt",       label: "Prompt", type: "textarea", placeholder: "Use {{ $node.<id>.output }} to reference upstream" },
    { key: "systemPrompt", label: "System prompt (optional)", type: "textarea" },
    { key: "agentId",      label: "Agent ID (optional)", type: "text", placeholder: "ag-default" },
    { key: "model",        label: "Model (optional)", type: "model", placeholder: "claude-sonnet-4-6" },
    { key: "maxTokens",    label: "Max tokens", type: "number", placeholder: "1024" },
  ],
  "action.hermesRun":    [
    { key: "goal",           label: "Goal", type: "textarea", placeholder: "Research this week's AI policy news and write a brief. {{ $node.<id>.output }} works here." },
    { key: "model",          label: "Model (optional)", type: "model", placeholder: "claude-sonnet-4-6" },
    { key: "dollarCapCents", label: "Dollar cap in cents (default 500)", type: "number", placeholder: "500" },
    { key: "waitSeconds",    label: "Wait for result (seconds, 0 = fire-and-forget, max 600)", type: "number", placeholder: "0" },
  ],
  "action.httpRequest":  [
    { key: "url",    label: "URL", type: "text", placeholder: "https://..." },
    { key: "method", label: "Method", type: "select", options: ["GET","POST","PUT","DELETE","PATCH"] },
  ],
  "action.mcpCall":      [
    { key: "serverId", label: "MCP server id", type: "text", placeholder: "mcp-deepwiki" },
    { key: "toolName", label: "Tool name", type: "text", placeholder: "ask_question" },
    { key: "args",     label: "Args (JSON object)", type: "json" },
  ],
  "action.kbSearch":     [
    { key: "kbId",  label: "KB id", type: "text", placeholder: "kb-abc123" },
    { key: "query", label: "Query", type: "textarea", placeholder: "{{ $node.n1.output.question }}" },
    { key: "topK",  label: "Top K (default 5)", type: "number", placeholder: "5" },
  ],
  "logic.if":            [
    { key: "left",  label: "Left", type: "text", placeholder: "{{ $node.n1.output.score }}" },
    { key: "op",    label: "Operator", type: "select", options: ["==","!=",">","<",">=","<=","contains"] },
    { key: "right", label: "Right", type: "text", placeholder: "0.8" },
  ],
  "logic.setVar":        [
    { key: "name",  label: "Variable name", type: "text", placeholder: "myVar" },
    { key: "value", label: "Value (interpolated)", type: "text", placeholder: "{{ $node.n1.output }}" },
  ],
  "output.toast":        [
    { key: "message", label: "Message (interpolated)", type: "text", placeholder: "Done!" },
  ],
  "output.saveMemory":   [
    { key: "text", label: "Memory text", type: "text" },
  ],
  "workflow.call":       [
    { key: "workflowId", label: "Target workflow ID", type: "text", placeholder: "wf-..." },
    { key: "vars",       label: "Initial variables (JSON object)", type: "json" },
  ],
  "action.sendEmail":    [
    { key: "to",      label: "To (string or comma-separated)", type: "text", placeholder: "alice@example.com" },
    { key: "subject", label: "Subject (interpolated)", type: "text", placeholder: "Daily digest -- {{ $var.date }}" },
    { key: "body",    label: "Body (interpolated)",    type: "textarea", placeholder: "Hello — here is today's update:\n\n{{ $node.summary.output }}" },
    { key: "from",    label: "From override (optional)", type: "text", placeholder: "alerts@yourdomain.com" },
    { key: "replyTo", label: "Reply-to (optional)",      type: "text" },
    { key: "html",    label: "Body is HTML",              type: "checkbox" },
  ],
  "action.fetchFeeds":   [
    { key: "urls",       label: "Feed URLs (JSON array)", type: "json" },
    { key: "sinceHours", label: "Filter to last N hours (default 24)", type: "number", placeholder: "24" },
    { key: "max",        label: "Max items (default 100)", type: "number", placeholder: "100" },
  ],
  "action.wait":         [
    { key: "ms", label: "Sleep milliseconds (0..60000)", type: "number", placeholder: "1000" },
  ],
  "action.code":         [
    { key: "code", label: "JS body (return a value)", type: "textarea", placeholder: "// inputs[<nodeId>] = upstream output\n// vars[<name>] = workflow variable\nreturn { ok: true, ts: Date.now() };" },
  ],
  "output.note":         [
    { key: "text", label: "Note text (interpolated)", type: "textarea", placeholder: "Reminder: rotate the API key monthly." },
  ],
  "action.jsonParse":    [
    { key: "input", label: "Input string (interpolated)", type: "textarea", placeholder: "{{ $node.http.output.body }}" },
  ],
  "action.regex":        [
    { key: "input",   label: "Input (interpolated)", type: "textarea", placeholder: "{{ $node.fetch.output }}" },
    { key: "pattern", label: "Pattern", type: "text", placeholder: "version[:\\s]+(\\d+\\.\\d+\\.\\d+)" },
    { key: "flags",   label: "Flags (default 'i')", type: "text", placeholder: "i" },
    { key: "mode",    label: "Mode", type: "select", options: ["test","match","matchAll"] },
  ],
  "action.slack":        [
    { key: "webhookUrl", label: "Slack webhook URL (optional -- falls back to Marketplace Slack)", type: "text", placeholder: "Leave blank to use Marketplace" },
    { key: "channel",    label: "Channel (required when using OAuth bot, e.g. #general)", type: "text", placeholder: "#general" },
    { key: "text",       label: "Message (interpolated)", type: "textarea", placeholder: "✓ Daily digest sent ({{ $node.fetch.output.fetched }} feeds)." },
  ],
  "logic.switch":        [
    { key: "expression", label: "Expression (interpolated)", type: "text", placeholder: "{{ $node.classify.output.label }}" },
    { key: "cases",      label: "Cases (JSON object)", type: "json" },
    { key: "default",    label: "Default branch", type: "text", placeholder: "default" },
  ],
  "logic.filter":        [
    { key: "items",   label: "Items (interpolation or JSON array)", type: "textarea", placeholder: "{{ $node.fetch.output.items }}" },
    { key: "pattern", label: "Regex pattern", type: "text" },
    { key: "flags",   label: "Flags (default 'i')", type: "text", placeholder: "i" },
  ],
  "logic.map":           [
    { key: "items",    label: "Items", type: "textarea", placeholder: "{{ $node.fetch.output.items }}" },
    { key: "template", label: "Template ({{ $item.field }})", type: "textarea", placeholder: "- {{ $item.title }} ({{ $item.source }})" },
  ],
  "logic.sort":          [
    { key: "items", label: "Items", type: "textarea", placeholder: "{{ $node.fetch.output.items }}" },
    { key: "by",    label: "Sort by field (dot path)", type: "text", placeholder: "publishedAt" },
    { key: "order", label: "Order", type: "select", options: ["asc","desc"] },
  ],
  "logic.dedupe":        [
    { key: "items", label: "Items", type: "textarea", placeholder: "{{ $node.fetch.output.items }}" },
    { key: "by",    label: "Dedupe by field (optional)", type: "text", placeholder: "link" },
  ],
  "action.discord":      [
    { key: "webhookUrl", label: "Discord webhook URL (optional -- falls back to Marketplace Discord)", type: "text", placeholder: "Leave blank to use Marketplace" },
    { key: "content",    label: "Message (interpolated, <2000 chars)", type: "textarea" },
  ],
  "action.template":     [
    { key: "template", label: "Template string", type: "textarea", placeholder: "Hello {{ $var.name }} -- {{ $node.x.output }}" },
  ],
  "action.csv":          [
    { key: "items",   label: "Items", type: "textarea", placeholder: "{{ $node.fetch.output.items }}" },
    { key: "columns", label: "Columns (JSON array, optional)", type: "json" },
  ],
  "action.base64":       [
    { key: "input", label: "Input (interpolated)", type: "textarea" },
    { key: "mode",  label: "Mode", type: "select", options: ["encode","decode"] },
  ],
  "logic.iif":           [
    { key: "condition", label: "Condition (interpolated, truthy if non-empty)", type: "text" },
    { key: "thenValue", label: "Then value", type: "text" },
    { key: "elseValue", label: "Else value", type: "text" },
  ],
  "logic.length":        [
    { key: "input", label: "Input (array / string / object)", type: "text" },
  ],
  "logic.slice":         [
    { key: "input", label: "Input", type: "text" },
    { key: "start", label: "Start", type: "number" },
    { key: "end",   label: "End (optional)", type: "number" },
  ],
  "logic.join":          [
    { key: "items",     label: "Items", type: "textarea" },
    { key: "separator", label: "Separator (default ', ')", type: "text" },
  ],
  "action.uuid":         [],
  "action.now":          [
    { key: "tz", label: "Timezone (IANA, default UTC)", type: "text", placeholder: "America/New_York" },
  ],
  "action.replace":      [
    { key: "input",   label: "Input (interpolated)", type: "textarea" },
    { key: "search",  label: "Search", type: "text" },
    { key: "replace", label: "Replace (interpolated)", type: "text" },
    { key: "all",     label: "Replace all (default true)", type: "checkbox" },
    { key: "regex",   label: "Search is a regex", type: "checkbox" },
  ],
  "action.markdownToHtml":[
    { key: "input", label: "Markdown (interpolated)", type: "textarea" },
  ],
  "output.webhook":      [
    { key: "url",     label: "POST URL (interpolated)", type: "text" },
    { key: "body",    label: "Body (interpolated, defaults to JSON of outputs)", type: "textarea" },
    { key: "headers", label: "Headers (JSON object, optional)", type: "json" },
  ],
  "action.urlEncode":    [
    { key: "input", label: "Input (interpolated)", type: "text" },
  ],
  "action.urlDecode":    [
    { key: "input", label: "Input (interpolated)", type: "text" },
  ],
  "action.range":        [
    { key: "start", label: "Start (default 0)", type: "number" },
    { key: "end",   label: "End (exclusive)", type: "number" },
    { key: "step",  label: "Step (default 1)", type: "number" },
  ],
  "action.lowercase":    [{ key: "input", label: "Input (interpolated)", type: "text" }],
  "action.uppercase":    [{ key: "input", label: "Input (interpolated)", type: "text" }],
  "action.mattermost":   [
    { key: "webhookUrl", label: "Mattermost webhook URL (optional -- falls back to Marketplace)", type: "text" },
    { key: "text",       label: "Message (interpolated)", type: "textarea" },
  ],
  "action.rocketchat":   [
    { key: "webhookUrl", label: "Rocket.Chat webhook URL (optional -- falls back to Marketplace)", type: "text" },
    { key: "text",       label: "Message (interpolated)", type: "textarea" },
  ],
};

const NODE_TYPE_LIST = Object.keys(NODE_TYPE_LABELS);

// Palette grouping. Service-based buckets so the (now ~68-node)
// palette stays scannable. Order = display order.
const WFE_APP_TRIGGERS = new Set(["trigger.salesforce","trigger.microsoft","trigger.googleWorkspace","trigger.slack","trigger.github","trigger.gitlab","trigger.jira","trigger.confluence","trigger.hubspot","trigger.stripe","trigger.notion","trigger.zendesk","trigger.linear","trigger.asana","trigger.monday","trigger.servicenow","trigger.zoom","trigger.shopify","trigger.dropbox","trigger.airtable","trigger.twilio","trigger.intercom"]);
const WFE_GROUP_ORDER = ["Core Triggers","App Triggers","AI & Knowledge","Messaging","HTTP & Web","Data Transform","Logic & Flow","Output"];
const WFE_GROUP_DEFAULT_COLLAPSED = { "App Triggers": true, "Data Transform": true };
function _wfNodeGroup(type) {
  if (WFE_APP_TRIGGERS.has(type)) return "App Triggers";
  if (type.startsWith("trigger.")) return "Core Triggers";
  if (["action.aiChat","action.hermesRun","action.kbSearch","action.mcpCall"].includes(type)) return "AI & Knowledge";
  if (["action.slack","action.discord","action.sendEmail","action.mattermost","action.rocketchat"].includes(type)) return "Messaging";
  if (["action.httpRequest","action.fetchFeeds","output.webhook"].includes(type)) return "HTTP & Web";
  if (type.startsWith("logic.") || ["workflow.call","action.wait","action.code"].includes(type)) return "Logic & Flow";
  if (type.startsWith("output.")) return "Output";
  return "Data Transform";
}

// One-time CSS inject for the editor's own chrome (palette, config
// panel, top bar). Reactflow ships its own canvas styles via
// "reactflow/dist/style.css" already imported above.
const STYLE_ID = "cf-workflow-editor-style";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
.cf-wfe-root { position: fixed; inset: 0; z-index: 1000; display: grid;
  grid-template-rows: 56px 1fr; background: var(--cf-wfe-bg, #0b0f1a); color: var(--cf-wfe-text, #e2e8f0);
  font-family: inherit; }
.cf-wfe-topbar { display: flex; align-items: center; gap: 12px; padding: 0 18px;
  border-bottom: 1px solid rgba(127,127,127,0.25); }
.cf-wfe-title { font-size: 14px; font-weight: 700; flex: 1; }
.cf-wfe-body { display: grid; grid-template-columns: 220px 1fr 280px; overflow: hidden; min-height: 0; }
.cf-wfe-palette { border-right: 1px solid rgba(127,127,127,0.25); overflow-y: auto; padding: 12px 10px; }
.cf-wfe-palette h4 { font-size: 10px; color: rgba(127,127,127,0.85); margin: 14px 4px 6px;
  text-transform: uppercase; font-family: monospace; letter-spacing: 0.06em; font-weight: 700; }
.cf-wfe-palette h4:first-child { margin-top: 0; }
.cf-wfe-palette button { display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 10px;
  margin-bottom: 4px; border-radius: 8px; border: 1px solid rgba(127,127,127,0.25);
  background: rgba(127,127,127,0.05); color: inherit; cursor: pointer; font-family: inherit; font-size: 12px;
  text-align: left; transition: background 140ms ease, transform 140ms cubic-bezier(0.16,1,0.3,1); }
.cf-wfe-palette button:hover { background: rgba(91,163,201,0.15); transform: translateX(2px); }
.cf-wfe-palette .cf-wfe-pal-icon { font-size: 14px; flex-shrink: 0; }
.cf-wfe-palette .cf-wfe-pal-desc { font-size: 10px; opacity: 0.6; font-family: monospace; }
.cf-wfe-pal-search { width: 100%; padding: 7px 10px; border-radius: 8px; border: 1px solid rgba(127,127,127,0.30);
  background: rgba(127,127,127,0.06); color: inherit; font-family: inherit; font-size: 12px; box-sizing: border-box;
  margin-bottom: 10px; }
.cf-wfe-pal-search:focus { outline: none; border-color: #5ba3c9; box-shadow: 0 0 0 3px rgba(91,163,201,0.18); }
.cf-wfe-pal-grouphead { display: flex; align-items: center; gap: 6px; cursor: pointer; user-select: none;
  margin: 14px 4px 6px !important; }
.cf-wfe-pal-group:first-child .cf-wfe-pal-grouphead { margin-top: 4px !important; }
.cf-wfe-pal-grouphead:hover { color: #5ba3c9; }
.cf-wfe-pal-chev { display: inline-block; font-size: 10px; transition: transform .14s ease; }
.cf-wfe-pal-count { font-size: 9px; opacity: 0.6; font-family: monospace; background: rgba(127,127,127,0.12);
  border-radius: 8px; padding: 1px 6px; }
.cf-wfe-canvas { position: relative; min-height: 0; }
.cf-wfe-config { border-left: 1px solid rgba(127,127,127,0.25); overflow-y: auto; padding: 14px 14px; }
.cf-wfe-config h4 { font-size: 11px; margin: 0 0 10px; font-family: monospace;
  text-transform: uppercase; letter-spacing: 0.05em; color: rgba(127,127,127,0.85); font-weight: 700; }
.cf-wfe-config .cf-wfe-empty { font-size: 11px; opacity: 0.6; padding-top: 12px; }
.cf-wfe-config label { display: block; font-size: 10px; color: rgba(127,127,127,0.85);
  font-family: monospace; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.04em; }
.cf-wfe-config input, .cf-wfe-config textarea, .cf-wfe-config select {
  width: 100%; padding: 7px 10px; border-radius: 7px;
  border: 1px solid rgba(127,127,127,0.30); background: rgba(127,127,127,0.06);
  color: inherit; font-family: monospace; font-size: 11px; margin-bottom: 12px; box-sizing: border-box; }
.cf-wfe-config textarea { min-height: 80px; resize: vertical; line-height: 1.5; }
.cf-wfe-btn { padding: 7px 14px; border-radius: 8px; border: 1px solid rgba(127,127,127,0.30);
  background: rgba(127,127,127,0.06); color: inherit; font-family: inherit; font-size: 12px; cursor: pointer; }
.cf-wfe-btn.primary { background: #5ba3c9; color: #fff; border-color: #5ba3c9; font-weight: 700; }
.cf-wfe-btn:hover { background: rgba(91,163,201,0.18); }
.cf-wfe-btn.primary:hover { background: #4a92b8; }

/* reactflow node default: thin card */
.react-flow__node-default {
  font-family: inherit !important; font-size: 11px !important;
  background: rgba(127,127,127,0.08) !important; color: inherit !important;
  border: 1px solid rgba(127,127,127,0.30) !important; border-radius: 8px !important;
  padding: 8px 12px !important; min-width: 140px !important;
}
.react-flow__node-default.selected { border-color: #5ba3c9 !important; box-shadow: 0 0 0 2px rgba(91,163,201,0.25) !important; }
.cf-wfe-ai { display: flex; flex-direction: column; height: 100%; min-height: 0; gap: 8px; }
.cf-wfe-ai-head { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  color: rgba(127,127,127,0.85); font-family: monospace; display: flex; justify-content: space-between;
  align-items: baseline; padding-bottom: 6px; border-bottom: 1px solid rgba(127,127,127,0.18); }
.cf-wfe-ai-scroll { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column;
  gap: 10px; padding-right: 2px; }
.cf-wfe-ai-empty { padding: 10px 4px; }
.cf-wfe-ai-msg { display: flex; flex-direction: column; gap: 6px; }
.cf-wfe-ai-msg.user { align-items: flex-end; }
.cf-wfe-ai-bubble { padding: 8px 11px; border-radius: 10px; font-size: 12px; line-height: 1.5;
  white-space: pre-wrap; word-break: break-word; max-width: 100%; }
.cf-wfe-ai-bubble.user { background: rgba(91,163,201,0.18); border: 1px solid rgba(91,163,201,0.35);
  color: inherit; }
.cf-wfe-ai-bubble.ai { background: rgba(127,127,127,0.08); border: 1px solid rgba(127,127,127,0.20); }
.cf-wfe-ai-cursor { display: inline-block; margin-left: 2px; color: #5ba3c9; font-weight: 700;
  animation: cfWfeBlink 0.9s step-end infinite; }
@keyframes cfWfeBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
.cf-wfe-ai-proposal { border: 1px solid rgba(91,163,201,0.35); border-radius: 10px; padding: 8px 10px;
  background: rgba(91,163,201,0.06); display: flex; flex-direction: column; gap: 6px; }
.cf-wfe-ai-proposal-head { font-size: 10px; color: #5ba3c9; font-family: monospace;
  text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
.cf-wfe-ai-proposal-btns { display: flex; gap: 6px; }
.cf-wfe-ai-proposal-btns .cf-wfe-btn { padding: 5px 10px; font-size: 11px; flex: 1; }
.cf-wfe-ai-input { display: flex; gap: 6px; align-items: flex-end; border-top: 1px solid rgba(127,127,127,0.18);
  padding-top: 8px; }
.cf-wfe-ai-input textarea { flex: 1; min-height: 44px; max-height: 120px; resize: vertical;
  padding: 8px 10px; border-radius: 8px; border: 1px solid rgba(127,127,127,0.30);
  background: rgba(127,127,127,0.06); color: inherit; font-family: inherit; font-size: 12px;
  margin-bottom: 0; box-sizing: border-box; line-height: 1.5; }
.cf-wfe-ai-input .cf-wfe-btn { padding: 8px 14px; font-size: 11px; flex-shrink: 0; }
`;
  document.head.appendChild(s);
}

let _wfNodeIdCounter = 1;
function _wfFreshNodeId(existing) {
  // Find smallest "n<N>" not already used.
  const used = new Set(existing.map(n => n.id));
  for (let i = 1; i < 10000; i++) {
    const id = "n" + i;
    if (!used.has(id)) return id;
  }
  _wfNodeIdCounter++;
  return "n" + (Date.now().toString(36)) + _wfNodeIdCounter;
}

function _wfRenderField(field, value, onChange) {
  const common = { value: value == null ? "" : (typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)) };
  if (field.type === "textarea") {
    return <textarea {...common} placeholder={field.placeholder||""} onChange={e => onChange(e.target.value)} />;
  }
  if (field.type === "select") {
    return (
      <select value={value || ""} onChange={e => onChange(e.target.value)}>
        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === "json") {
    return <textarea {...common} placeholder='{"key":"value"}' onChange={e => {
      try { onChange(JSON.parse(e.target.value)); } catch { onChange(e.target.value); }
    }} />;
  }
  if (field.type === "number") {
    return <input type="number" {...common} placeholder={field.placeholder||""} onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  }
  return <input type="text" {...common} placeholder={field.placeholder||""} onChange={e => onChange(e.target.value)} />;
}

function _wfNodeLabel(node) {
  const meta = NODE_TYPE_LABELS[node?.data?.cfType];
  return meta ? (meta.icon + " " + meta.label) : (node?.data?.label || node?.id || "?");
}

function _toRfNodes(defNodes) {
  if (!Array.isArray(defNodes)) return [];
  return defNodes.map(n => ({
    id: n.id,
    position: n.position || { x: 0, y: 0 },
    data: { ...(n.data || {}), cfType: n.type, label: _wfNodeLabel({ data: { cfType: n.type } }) },
    type: "default",
  }));
}
function _toRfEdges(defEdges) {
  if (!Array.isArray(defEdges)) return [];
  return defEdges.map(e => ({ id: e.id, source: e.source, target: e.target, animated: true }));
}

export default function WorkflowEditor({ initialValue, onSave, onCancel, theme }) {
  const t = theme || {};
  // Apply theme tokens as CSS vars so the injected stylesheet picks them up.
  useEffect(() => {
    const root = document.documentElement;
    if (t.bg)   root.style.setProperty("--cf-wfe-bg", t.bg);
    if (t.text) root.style.setProperty("--cf-wfe-text", t.text);
    return () => {
      root.style.removeProperty("--cf-wfe-bg");
      root.style.removeProperty("--cf-wfe-text");
    };
  }, [t.bg, t.text]);

  const initialNodes = useMemo(() => _toRfNodes(initialValue?.nodes), [initialValue]);
  const initialEdges = useMemo(() => _toRfEdges(initialValue?.edges), [initialValue]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedId, setSelectedId] = useState(null);

  // Fetch the org's enabled-model list once on mount so the AI-chat
  // node config can render a <select> instead of a freeform text field.
  // Fail silently -- if /api/models is unreachable, the dispatcher
  // falls back to a text input so the user isn't blocked.
  const [modelOptions, setModelOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/models");
        if (!r.ok) return;
        const json = await r.json();
        if (!json || typeof json !== "object" || cancelled) return;
        const ids = Object.entries(json)
          .filter(([, o]) => !o || o.enabled !== false)
          .map(([id]) => id)
          .sort();
        if (!cancelled) setModelOptions(ids);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Field-renderer dispatcher with closure over modelOptions. Falls
  // through to the module-level _wfRenderField for non-"model" types.
  function renderField(field, value, onChange) {
    if (field.type === "model") {
      const options = modelOptions.length ? modelOptions : (value ? [value] : []);
      return (
        <select value={value || ""} onChange={e => onChange(e.target.value)}>
          {!value && <option value="">(default — use agent's model)</option>}
          {options.map(id => <option key={id} value={id}>{id}</option>)}
          {value && !options.includes(value) && (
            <option value={value}>{value} (custom)</option>
          )}
        </select>
      );
    }
    return _wfRenderField(field, value, onChange);
  }

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({ ...params, id: "e" + Date.now().toString(36), animated: true }, eds));
  }, [setEdges]);

  const onNodeClick = useCallback((_, node) => setSelectedId(node.id), []);
  const onPaneClick = useCallback(() => setSelectedId(null), []);

  // AI Build chat state. Lives entirely in the editor component;
  // nothing persists -- close the modal to discard.
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiStreaming, setAiStreaming] = useState(false);
  const aiScrollerRef = useRef(null);
  // Palette search + per-group collapse state.
  const [palSearch, setPalSearch] = useState("");
  const [palCollapsed, setPalCollapsed] = useState(() => ({ ...WFE_GROUP_DEFAULT_COLLAPSED }));
  useEffect(() => {
    if (aiScrollerRef.current) aiScrollerRef.current.scrollTop = aiScrollerRef.current.scrollHeight;
  }, [aiMessages]);

  // Build the AI system prompt: catalog + current state + rules.
  function buildAiSystemPrompt() {
    const catalog = Object.entries(NODE_TYPE_LABELS).map(([k, m]) => "  " + k + ": " + (m.desc || m.label)).join("\n");
    const current = JSON.stringify({
      nodes: nodes.map(n => ({ id: n.id, type: n.data?.cfType || "trigger.manual", position: n.position, data: Object.fromEntries(Object.entries(n.data || {}).filter(([k]) => k !== "cfType" && k !== "label")) })),
      edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target })),
    }, null, 2);
    return "You are the ChatFort Visual Workflow Architect. The user is editing a workflow on a visual canvas (React Flow). Your job is to help them build the workflow they describe, end-to-end.\n\n# Available node types (use the exact id as the type field)\n" + catalog + "\n\n# Current workflow state\n```json\n" + current + "\n```\n\n# Output rules\n- When the user asks you to BUILD, CHANGE, ADD, REMOVE, or CONNECT nodes, you MUST emit a complete proposed workflow as a single fenced code block tagged \"workflow\". The body is a JSON object with two keys: \"nodes\" (array of {id, type, position:{x,y}, data:{...}}) and \"edges\" (array of {id, source, target}).\n- Use the EXACT node type ids from the catalog above. Invented type ids will be rejected.\n- Lay out new nodes left-to-right starting at x=80, stepping by 220 horizontally, y=120. Branches step y by 160.\n- Every workflow needs ONE trigger.* node and at least one downstream node. Connect with edges using format {\"id\":\"e1\",\"source\":\"n1\",\"target\":\"n2\"}.\n- Fill in the node data with sensible defaults (model ids, prompts, URLs, etc.) the user can later tweak in the right-hand config pane.\n- BEFORE the fenced block, write ONE short sentence describing what you built. NO long preamble.\n- If the user is asking a question rather than a build request, answer briefly and skip the fenced block.";
  }

  // Parse a workflow JSON fenced block out of an assistant message.
  function extractWorkflowFromMessage(content) {
    if (!content) return null;
    const re = /```workflow\s*\n([\s\S]*?)\n```/;
    const m = content.match(re);
    if (!m) return null;
    try {
      const parsed = JSON.parse(m[1]);
      if (!parsed || !Array.isArray(parsed.nodes)) return null;
      return parsed;
    } catch { return null; }
  }

  // Validate a proposed workflow before applying.
  function validateProposal(proposal) {
    const errs = [];
    if (!Array.isArray(proposal.nodes) || proposal.nodes.length === 0) errs.push("no nodes");
    for (const n of (proposal.nodes || [])) {
      if (!n.id) errs.push("node missing id");
      if (!n.type || !NODE_TYPE_LABELS[n.type]) errs.push("unknown node type: " + n.type);
    }
    if (proposal.edges && !Array.isArray(proposal.edges)) errs.push("edges must be an array");
    for (const e of (proposal.edges || [])) {
      if (!e.source || !e.target) errs.push("edge missing source/target");
    }
    return errs;
  }

  // Apply a proposed workflow to the canvas.
  function applyProposal(proposal, mode) {
    // mode: "replace" -> wipe current and use proposal. "merge" -> append.
    const errs = validateProposal(proposal);
    if (errs.length) { window.alert("Cannot apply: " + errs.join("; ")); return; }
    const newRfNodes = _toRfNodes(proposal.nodes);
    const newRfEdges = _toRfEdges(proposal.edges || []);
    if (mode === "replace") {
      setNodes(newRfNodes);
      setEdges(newRfEdges);
      setSelectedId(null);
    } else {
      // Merge: rename incoming ids so they don't collide.
      const existingIds = new Set(nodes.map(n => n.id));
      const idMap = {};
      const next = newRfNodes.map(n => {
        let nid = n.id;
        while (existingIds.has(nid)) nid = "n" + (Date.now().toString(36)) + Math.floor(Math.random()*999);
        idMap[n.id] = nid;
        existingIds.add(nid);
        return { ...n, id: nid };
      });
      const nextEdges = newRfEdges.map(e => ({ ...e, source: idMap[e.source] || e.source, target: idMap[e.target] || e.target }));
      setNodes(nn => nn.concat(next));
      setEdges(ee => ee.concat(nextEdges));
    }
    setAiOpen(false);
  }

  // Call the chat API. Streams tokens into the assistant message.
  async function sendAiMessage() {
    const text = aiInput.trim();
    if (!text || aiStreaming) return;
    const userMsg = { id: "u-"+Math.random().toString(36).slice(2,9), role: "user", content: text };
    const aiId = "a-"+Math.random().toString(36).slice(2,9);
    setAiMessages(p => [...p, userMsg, { id: aiId, role: "assistant", content: "", streaming: true }]);
    setAiInput("");
    setAiStreaming(true);
    try {
      const sys = buildAiSystemPrompt();
      // /api/chat is auth-gated in server mode. Pull the JWT the main
      // app persisted so the architect call isn't rejected with
      // "No token".
      let _tok = null;
      try { _tok = localStorage.getItem("cf_auth_token"); } catch {}
      const r = await fetch("/api/chat", {
        method: "POST",
        headers: _tok
          ? { "Content-Type": "application/json", "Authorization": "Bearer " + _tok }
          : { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: "anthropic",
          model: "claude-haiku-4-5-20251001",
          rawMessages: [...aiMessages, userMsg].map(m => ({ role: m.role, content: m.content })),
          sysprompt: sys,
          maxTokens: 4096,
          temperature: 0.4,
          stream: true,
          webSearch: false,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(()=>({}));
        throw new Error(d.error || ("HTTP " + r.status));
      }
      const ct = r.headers.get("content-type") || "";
      if (ct.includes("text/event-stream")) {
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buf = "", acc = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data:")) continue;
            try {
              const evt = JSON.parse(line.slice(5).trim());
              if (typeof evt.t === "string") {
                acc += evt.t;
                setAiMessages(p => p.map(m => m.id === aiId ? { ...m, content: acc } : m));
              }
            } catch {}
          }
        }
        setAiMessages(p => p.map(m => m.id === aiId ? { ...m, streaming: false } : m));
      } else {
        const d = await r.json().catch(()=>({}));
        setAiMessages(p => p.map(m => m.id === aiId ? { ...m, content: d.text || "(no response)", streaming: false } : m));
      }
    } catch (err) {
      setAiMessages(p => p.map(m => m.id === aiId ? { ...m, content: "Error: " + err.message, streaming: false, error: true } : m));
    } finally {
      setAiStreaming(false);
    }
  }

  function addNode(cfType) {
    const id = _wfFreshNodeId(nodes);
    const meta = NODE_TYPE_LABELS[cfType] || { icon: "?", label: cfType };
    const node = {
      id,
      position: { x: 80 + (nodes.length % 5) * 180, y: 80 + Math.floor(nodes.length / 5) * 110 },
      data: { cfType, label: meta.icon + " " + meta.label },
      type: "default",
    };
    setNodes((nn) => nn.concat(node));
    setSelectedId(id);
  }

  function updateNodeData(id, key, value) {
    setNodes((nn) => nn.map(n => n.id === id ? { ...n, data: { ...n.data, [key]: value } } : n));
  }

  function selected() {
    return nodes.find(n => n.id === selectedId) || null;
  }

  function save() {
    // Project reactflow shape back to definition shape.
    const outNodes = nodes.map(n => {
      const { cfType, label, ...rest } = n.data || {};
      return { id: n.id, type: cfType || "trigger.manual", position: n.position, data: rest };
    });
    const outEdges = edges.map(e => ({ id: e.id, source: e.source, target: e.target }));
    onSave({ nodes: outNodes, edges: outEdges });
  }

  // Group palette by category (trigger / action / logic / output).
  const grouped = useMemo(() => {
    const g = {};
    for (const name of WFE_GROUP_ORDER) g[name] = [];
    for (const t of NODE_TYPE_LIST) {
      const grp = _wfNodeGroup(t);
      (g[grp] = g[grp] || []).push(t);
    }
    return g;
  }, []);
  // Filtered view honoring the palette search box.
  const palQuery = palSearch.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!palQuery) return grouped;
    const out = {};
    for (const [name, types] of Object.entries(grouped)) {
      const hits = types.filter(t => {
        const m = NODE_TYPE_LABELS[t] || {};
        return t.toLowerCase().includes(palQuery) || (m.label || "").toLowerCase().includes(palQuery) || (m.desc || "").toLowerCase().includes(palQuery);
      });
      if (hits.length) out[name] = hits;
    }
    return out;
  }, [grouped, palQuery]);

  const sel = selected();
  const schema = sel ? (NODE_CONFIG_SCHEMA[sel.data?.cfType] || []) : [];

  return (
    <div className="cf-wfe-root">
      <div className="cf-wfe-topbar">
        <span className="cf-wfe-title">Visual workflow editor</span>
        <span style={{ fontSize: 10, opacity: 0.55, fontFamily: "monospace" }}>{nodes.length} node{nodes.length===1?"":"s"} · {edges.length} edge{edges.length===1?"":"s"}</span>
        <button className={"cf-wfe-btn" + (aiOpen ? " primary" : "")} onClick={() => setAiOpen(p => !p)} title="Describe the workflow you want and let AI build it">
          {aiOpen ? "Close AI" : "🤖 AI Build"}
        </button>
        <button className="cf-wfe-btn" onClick={onCancel}>Cancel</button>
        <button className="cf-wfe-btn primary" onClick={save}>Save</button>
      </div>
      <div className="cf-wfe-body">
        <div className="cf-wfe-palette">
          <input
            className="cf-wfe-pal-search"
            value={palSearch}
            onChange={e => setPalSearch(e.target.value)}
            placeholder="Search nodes…"
          />
          {WFE_GROUP_ORDER.filter(cat => filteredGroups[cat]?.length).map(cat => {
            const types = filteredGroups[cat];
            // When searching, force every matching group open.
            const isCollapsed = palQuery ? false : !!palCollapsed[cat];
            return (
              <div key={cat} className="cf-wfe-pal-group">
                <h4 className="cf-wfe-pal-grouphead" onClick={() => setPalCollapsed(p => ({ ...p, [cat]: !p[cat] }))}>
                  <span className="cf-wfe-pal-chev" style={{ transform: isCollapsed ? "rotate(-90deg)" : "none" }}>▾</span>
                  <span style={{ flex: 1 }}>{cat}</span>
                  <span className="cf-wfe-pal-count">{types.length}</span>
                </h4>
                {!isCollapsed && types.map(type => {
                  const m = NODE_TYPE_LABELS[type];
                  return (
                    <button key={type} onClick={() => addNode(type)}>
                      <span className="cf-wfe-pal-icon">{m.icon}</span>
                      <span>
                        <div>{m.label}</div>
                        <div className="cf-wfe-pal-desc">{m.desc}</div>
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          {palQuery && Object.keys(filteredGroups).length === 0 && (
            <div style={{ fontSize: 11, opacity: 0.6, padding: "12px 4px" }}>No nodes match "{palSearch}".</div>
          )}
        </div>
        <div className="cf-wfe-canvas">
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              deleteKeyCode={["Backspace","Delete"]}
            >
              <Background gap={20} size={1} color="rgba(127,127,127,0.25)" />
              <Controls showInteractive={false} />
              <MiniMap pannable zoomable style={{ background: "rgba(127,127,127,0.08)" }} />
            </ReactFlow>
          </ReactFlowProvider>
        </div>
        <div className="cf-wfe-config">
          {aiOpen ? (
            <div className="cf-wfe-ai">
              <div className="cf-wfe-ai-head">
                <span>🤖 AI Workflow Architect</span>
                <span style={{ fontSize: 9, opacity: 0.6, fontFamily: "monospace" }}>{aiMessages.length} msg</span>
              </div>
              <div className="cf-wfe-ai-scroll" ref={aiScrollerRef}>
                {aiMessages.length === 0 && (
                  <div className="cf-wfe-ai-empty">
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Describe what you want to build.</div>
                    <div style={{ fontSize: 11, opacity: 0.7, lineHeight: 1.55 }}>
                      Examples:
                      <ul style={{ margin: "6px 0 0 14px", padding: 0 }}>
                        <li>Daily 7am Slack digest from RSS feeds</li>
                        <li>Email triage: read inbox, classify, draft replies</li>
                        <li>When webhook fires, search KB and post to Discord</li>
                        <li>PR notifier: fetch GitHub, format, send to Slack</li>
                      </ul>
                    </div>
                  </div>
                )}
                {aiMessages.map(m => {
                  if (m.role === "user") {
                    return (<div key={m.id} className="cf-wfe-ai-msg user"><div className="cf-wfe-ai-bubble user">{m.content}</div></div>);
                  }
                  const proposal = !m.streaming ? extractWorkflowFromMessage(m.content) : null;
                  // Strip the workflow block from display text -- show
                  // only the model's prose intro.
                  const display = (m.content || "").replace(/```workflow[\s\S]*?```/, "").trim();
                  return (
                    <div key={m.id} className="cf-wfe-ai-msg ai">
                      <div className="cf-wfe-ai-bubble ai">
                        {display || (m.streaming ? "…" : "(empty)")}
                        {m.streaming && <span className="cf-wfe-ai-cursor">▌</span>}
                      </div>
                      {proposal && (
                        <div className="cf-wfe-ai-proposal">
                          <div className="cf-wfe-ai-proposal-head">
                            ✦ Proposed: {proposal.nodes.length} node{proposal.nodes.length===1?"":"s"}, {proposal.edges?.length || 0} edge{proposal.edges?.length===1?"":"s"}
                          </div>
                          <div className="cf-wfe-ai-proposal-btns">
                            <button className="cf-wfe-btn primary" onClick={() => applyProposal(proposal, "replace")}>Apply (replace)</button>
                            <button className="cf-wfe-btn" onClick={() => applyProposal(proposal, "merge")}>Add to canvas</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="cf-wfe-ai-input">
                <textarea
                  value={aiInput}
                  onChange={e => setAiInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendAiMessage(); } }}
                  placeholder={aiStreaming ? "Thinking…" : "Describe the workflow you want to build…"}
                  disabled={aiStreaming}
                  rows={2}
                />
                <button className="cf-wfe-btn primary" onClick={sendAiMessage} disabled={!aiInput.trim() || aiStreaming}>
                  {aiStreaming ? "…" : "SEND"}
                </button>
              </div>
            </div>
          ) : !sel ? (
            <div className="cf-wfe-empty">Click a node on the canvas to configure it. Click a palette item to add a new node. Or click 🤖 <strong>AI Build</strong> to describe what you want.</div>
          ) : (
            <>
              <h4>
                {NODE_TYPE_LABELS[sel.data?.cfType]?.icon} {NODE_TYPE_LABELS[sel.data?.cfType]?.label || sel.data?.cfType}
                {WFE_APP_TRIGGERS.has(sel.data?.cfType) && (
                  <span style={{ marginLeft: 8, fontSize: 8, fontWeight: 800, letterSpacing: "0.08em", padding: "2px 6px", borderRadius: 4, background: "rgba(245,158,11,0.18)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.4)", verticalAlign: "middle" }}>PREVIEW</span>
                )}
              </h4>
              {WFE_APP_TRIGGERS.has(sel.data?.cfType) && (
                <div style={{ fontSize: 11, lineHeight: 1.55, color: "rgba(245,158,11,0.95)", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.30)", borderRadius: 8, padding: "9px 11px", marginBottom: 12 }}>
                  <strong>Preview integration.</strong> You can design and save a workflow with this trigger, but live <em>{NODE_TYPE_LABELS[sel.data?.cfType]?.label}</em> event delivery isn't wired up yet — it won't auto-fire on real events. To run this workflow today, add a <strong>Manual</strong> or <strong>Schedule</strong> trigger.
                </div>
              )}
              <label>NODE ID</label>
              <input value={sel.id} readOnly style={{ opacity: 0.6 }} />
              {schema.length === 0 ? (
                <div className="cf-wfe-empty">No config for this node type.</div>
              ) : schema.map(field => {
                const val = sel.data?.[field.key];
                return (
                  <div key={field.key}>
                    <label>{field.label}</label>
                    {renderField(field, val, (v) => updateNodeData(sel.id, field.key, v))}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
